import React from 'react';
import { useDropzone } from 'react-dropzone';
import { t } from '../utils/localization/localization';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import TextField from '@mui/material/TextField';

/**
 * Reusable file upload component with text input alternative
 * Used across CV, Job, and Company analysis pages
 */
const FileUpload = ({
  onTextChange,
  onDrop,
  onRemoveFile,
  text,
  files,
  textLabel = t('fileUploadComponent.textLabel'),
  dropzoneText = t('fileUploadComponent.dropzoneText'),
  required = false,
  fileUploadOptional = false,
  acceptedFileTypes = {
    'text/plain': ['.txt'],
    'application/pdf': ['.pdf'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
  }
}) => {
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: acceptedFileTypes
  });

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      {/* File dropzone */}
      <Paper
        {...getRootProps()}
        elevation={0}
        sx={{
          border: '2px dashed #cccccc',
          borderRadius: 2,
          p: 3,
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: '#f8f8f8',
          transition: 'all 0.3s',
          '&:hover': {
            borderColor: '#2196f3',
            backgroundColor: '#f1f8fe'
          }
        }}
      >
        <input {...getInputProps()} />
        <CloudUploadIcon sx={{ fontSize: 48, color: '#9e9e9e', mb: 1 }} />
        <Typography variant="body1">{dropzoneText}</Typography>
        {fileUploadOptional && (
          <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', mt: 1 }}>
            {t('fileUploadComponent.optionalFiles')}
          </Typography>
        )}
        <Typography variant="body2" color="textSecondary">
          {t('fileUploadComponent.acceptedFormats')}
        </Typography>
      </Paper>

      {/* List of uploaded files */}
      {files.length > 0 && (
        <Paper elevation={1} sx={{ mt: 2, p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            {t('fileUploadComponent.uploadedFiles')}:
          </Typography>
          <List dense>
            {files.map((file, index) => (
              <ListItem
                key={index}
                secondaryAction={
                  <IconButton edge="end" aria-label="delete" onClick={() => onRemoveFile(index)}>
                    <DeleteIcon />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={file.name}
                  secondary={`${(file.size / 1024).toFixed(2)} KB`}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {/* Text input alternative */}
      <TextField
        label={textLabel}
        multiline
        rows={6}
        fullWidth
        value={text}
        onChange={onTextChange}
        sx={{ mt: 3 }}
        variant="outlined"
        required={required}
        error={required && text.trim() === ''}
        helperText={required && text.trim() === '' ? t('validation.required') : ''}
      />
    </Box>
  );
};

export default FileUpload;
